// Contract detail — IA mirrored from the Claude Design handoff:
//
//   • Breadcrumb row    Contracts › {type} › {shortName}
//   • Header            status + flag chips, title, subtitle, action buttons
//   • Callouts          auto-renew window / expiring / already-expired
//   • Segmented tabs    Overview · Clauses · Timeline · Files · Related
//   • Detail grid       main column + sticky rail (Properties · People · Activity)
//
// Per-card inline editing (the previous 13 useCardEdit hooks) is gone. The
// top-right "Edit" button routes to `/contracts/:id/edit` — a dedicated
// editor surface is the follow-up ("we can figure out how to edit from
// there"). Scan / Terminate / Delete / Relink / Detailed summary still live
// here as modals, triggered from the More menu next to Edit.

import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { SkeletonDetail } from '../components/Skeleton';
import { useToast } from '../components/ToastProvider';
import ScanDocumentModal from '../components/ScanDocumentModal';
import { deriveFlags } from '../components/SignalStrip';
import { useAuth } from '../context/AuthContext';
import ContractDetailAlerts from '../features/contracts/ContractDetailAlerts';
import ContractDetailHeader from '../features/contracts/ContractDetailHeader';
import ContractPreviewPanel from '../features/contracts/ContractPreviewPanel';
import { defaultAgreementFile, previewFiles } from '../features/contracts/contractPreviewUtils';
import ContractFactStrip from '../features/contracts/ContractFactStrip';
import {
  buildClauses,
  buildFileTiles,
  buildTimeline,
  daysBetween,
  formatActivityEvent,
} from '../features/contracts/contractDetailUtils';
import ContractDetailMainTabs from '../features/contracts/ContractDetailMainTabs';
import ContractDetailModals from '../features/contracts/ContractDetailModals';
import ContractDetailRail from '../features/contracts/ContractDetailRail';
import ContractUploadFileModal from '../features/contracts/ContractUploadFileModal';
import useContractDetailActions from '../features/contracts/useContractDetailActions';
import useContractDetailData from '../features/contracts/useContractDetailData';
import useStructuredData from '../features/structured-data/useStructuredData';
import useContractFileActions from '../features/contracts/useContractFileActions';
import useContractFileUploadWorkflow from '../features/contracts/useContractFileUploadWorkflow';

// -----------------------------------------------------------------------
// Main component
// -----------------------------------------------------------------------

export default function ContractDetail({ apiBase }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { canWriteContracts, isAdmin, can } = useAuth();

  const {
    activityEvents,
    contract,
    contractFiles,
    error,
    loading,
    refresh: fetchContract,
    setContract,
  } = useContractDetailData(id);

  const [tab, setTab] = useState('overview');
  const [preview, setPreview] = useState(null);
  const openFilePreview = (file) => setPreview({ contractId: id, key: file.key });
  // Structured data outputs (admin-defined extraction schemas). Loaded
  // alongside the contract; the tab only appears when a schema applies to
  // this contract or an output already exists.
  const structuredData = useStructuredData(id);
  const {
    beginClauseEdit,
    cancelClauseEdit,
    clauseDraft,
    clauseError,
    clauseSaving,
    deleteDriveFolder,
    deleting,
    dismissReminder,
    dsDraft,
    dsEditing,
    dsError,
    dsSaving,
    editingClauseNum,
    handleDelete,
    handleDetailedSummarySave,
    handleMarkReviewed,
    handlePublishToDrive,
    handleStatusChange,
    handleTerminate,
    handleRegenerateKeyRisks,
    handleClearKeyRisks,
    handleSaveKeyTerms,
    regeneratingKeyRisks,
    publishing,
    newExecutedDate,
    newStatus,
    openClause,
    openStatusModal,
    saveClauseEdit,
    setClauseDraft,
    setDeleteDriveFolder,
    setDsDraft,
    setDsEditing,
    setNewExecutedDate,
    setNewStatus,
    setOpenClause,
    setShowDeleteConfirm,
    setShowDetailedSummaryModal,
    setShowScanModal,
    setShowStatusModal,
    setShowTerminateConfirm,
    setTerminationReason,
    showDeleteConfirm,
    showDetailedSummaryModal,
    showScanModal,
    showStatusModal,
    showTerminateConfirm,
    terminationReason,
  } = useContractDetailActions({
    contract,
    contractId: id,
    navigate,
    refreshContract: fetchContract,
    setContract,
    showToast,
  });

  const {
    closeUploadModal,
    handleConfirmUpload,
    handleFilePicked,
    openUploadModal,
    pendingUpload,
    rescanInitialFile,
    setPendingUpload,
    setRescanInitialFile,
    setUploadDragOver,
    showUploadModal,
    uploadAnalyzing,
    uploadBusy,
    uploadDragOver,
  } = useContractFileUploadWorkflow({
    canWriteContracts,
    contractId: id,
    refreshContract: fetchContract,
    setShowScanModal,
    showToast,
  });

  const {
    cancelFileEdit,
    closeFileDeleteConfirm,
    deleteContractDriveFolderWithLastFile,
    deleteContractWithLastFile,
    deleteDriveFile,
    deletingFileId,
    editingFileId,
    fileDeleteTarget,
    fileDraft,
    handleDeleteFile,
    handleMarkAsExecuted,
    markingFileId,
    openFileDeleteConfirm,
    openFileEdit,
    saveFileMeta,
    savingFileMeta,
    setDeleteContractDriveFolderWithLastFile,
    setDeleteContractWithLastFile,
    setDeleteDriveFile,
    setFileDraft,
  } = useContractFileActions({
    contractFiles,
    contractId: id,
    navigate,
    refreshContract: fetchContract,
    showToast,
  });

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------

  const flags = useMemo(() => deriveFlags(contract), [contract]);
  const clauses = useMemo(() => contract ? buildClauses(contract) : [], [contract]);

  const timeline = useMemo(() => {
    if (activityEvents) {
      return activityEvents.map(formatActivityEvent);
    }
    return contract ? buildTimeline(contract) : [];
  }, [activityEvents, contract]);

  const daysToExp = contract?.expirationDate
    ? daysBetween(new Date(), contract.expirationDate)
    : null;
  const daysTotal = (contract?.effectiveDate && contract?.expirationDate)
    ? Math.max(1, daysBetween(contract.effectiveDate, contract.expirationDate))
    : null;
  const daysElapsed = contract?.effectiveDate
    ? Math.max(0, daysBetween(contract.effectiveDate, new Date()))
    : null;
  const termPct = (daysTotal && daysElapsed != null)
    ? Math.max(0, Math.min(1, daysElapsed / daysTotal))
    : 0;

  // Days until the counterparty-notice deadline (auto-renewal cutoff).
  // Positive = still actionable. Negative = window already closed.
  // null = no deadline on file (we fall back to a simple days-to-expiry view).
  const daysToNoticeDate = contract?.autoRenewalNoticeDate
    ? daysBetween(new Date(), contract.autoRenewalNoticeDate)
    : null;
  const noticeWindowClosed = daysToNoticeDate != null && daysToNoticeDate <= 0;
  const noticeWindowOpen = daysToNoticeDate != null && daysToNoticeDate > 0;

  // Reminder dismissals — keyed to the underlying date so the alerts
  // re-arm automatically if the date later changes (the server clears
  // the column on PUT when the date shifts).
  const renewalReminderDismissed =
    !!contract?.renewalDismissedForDate &&
    contract.renewalDismissedForDate === contract.expirationDate;
  const noticeReminderDismissed =
    !!contract?.noticeDismissedForDate &&
    contract.noticeDismissedForDate === contract.autoRenewalNoticeDate;

  // Show the auto-renew callout when:
  //   • auto-renew flag is set and expiration is still ahead, AND
  //   • the notice window is either still open (actionable — surface ASAP
  //     even if expiration is ~year away), or
  //   • it closed recently enough (< 30 days past) that the user should
  //     know the decision was made by default, or
  //   • we have no notice date and expiration is within 120 days (legacy
  //     fallback — better than silence on old rows).
  //
  // Previously this fired only once daysToExp < 120, which meant that for
  // a contract with a long notice window (say 90 days before a multi-year
  // term), the alert didn't appear until AFTER the window had closed.
  const autoRenewWindow =
    flags.includes('auto-renew') &&
    daysToExp != null &&
    daysToExp > 0 &&
    (
      noticeWindowOpen ||
      (noticeWindowClosed && daysToNoticeDate > -30) ||
      (daysToNoticeDate == null && daysToExp < 120)
    );
  const isExpiringSoon = daysToExp != null && daysToExp > 0 && daysToExp <= 30;
  const isAlreadyExpired = daysToExp != null && daysToExp < 0 &&
    contract?.status !== 'terminated' && contract?.status !== 'expired';

  const fileTiles = useMemo(() => buildFileTiles(contract, contractFiles), [contract, contractFiles]);
  const filesForPreview = useMemo(() => previewFiles(fileTiles), [fileTiles]);
  const agreementFile = defaultAgreementFile(fileTiles, contract?.executedFileId);

  if (loading) {
    return <div className="animate-fade-in"><SkeletonDetail /></div>;
  }
  if (error) {
    return (
      <div className="canvas-inner animate-fade-in">
        <div className="callout red" style={{ alignItems: 'flex-start' }}>
          <div className="callout-icon"><AlertCircle className="w-4 h-4" /></div>
          <div className="callout-body">
            <strong>Contract not found.</strong>
            <div style={{ marginTop: 4, fontSize: 12 }}>{error}</div>
          </div>
          <Link to="/contracts" className="btn outline sm">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to contracts
          </Link>
        </div>
      </div>
    );
  }

  const c = contract;
  const additionalParties = Array.isArray(c.additionalCounterparties)
    ? c.additionalCounterparties.filter((party) => party && party.name)
    : [];
  const counterpartyContactName = c.counterpartyContactName || '';
  const counterpartyContactEmail = c.counterpartyContactEmail || '';
  const counterpartyContactPhone = c.counterpartyContactPhone || '';
  const counterpartyContactRole = c.counterpartyContactRole || '';
  const sdSchemas = structuredData.data?.schemas?.length || 0;
  const sdOutputs = structuredData.data?.outputs?.length || 0;
  const tabs = [
    { v: 'overview', l: 'Overview' },
    { v: 'clauses',  l: 'Key Clauses', ct: clauses.length },
    // Hidden until at least one schema targets this contract or an output
    // exists — no noise before an admin sets schemas up.
    ...(sdSchemas + sdOutputs > 0
      ? [{ v: 'data', l: 'Structured Data', ct: sdOutputs > 0 ? sdOutputs : null }]
      : []),
    { v: 'timeline', l: 'Timeline', ct: timeline.length },
    { v: 'files',    l: 'Files',    ct: fileTiles.length },
    { v: 'related',  l: 'Related',  ct:
      (c.parent ? 1 : 0)
      + (Array.isArray(c.amendments) ? c.amendments.length : 0)
      + (c.renewalOf ? 1 : 0)
      + (Array.isArray(c.renewedBy) ? c.renewedBy.length : 0)
      + (c.sourceRequestId ? 1 : 0)
    },
  ];

  return (
    <div className="canvas-inner animate-fade-in">
      {/* ================= Breadcrumb ================= */}
      <div className="row muted" style={{ fontSize: 12, marginBottom: 14, gap: 6 }}>
        <Link to="/contracts" style={{ color: 'var(--text-secondary)' }}>Contracts</Link>
        <ChevronRight className="w-3 h-3" style={{ opacity: 0.4 }} />
        <span className="tertiary">{c.contractType === 'Other' && c.contractTypeOther ? c.contractTypeOther : c.contractType || 'Contract'}</span>
        {c.shortName && (
          <>
            <ChevronRight className="w-3 h-3" style={{ opacity: 0.4 }} />
            <span className="mono" style={{ color: 'var(--text-primary)' }}>{c.shortName}</span>
          </>
        )}
      </div>

      {/* ================= Header ================= */}
      <ContractDetailHeader
        additionalParties={additionalParties}
        canWriteContracts={canWriteContracts}
        contract={c}
        contractId={id}
        flags={flags}
        isAdmin={isAdmin}
        onOpenDeleteConfirm={() => setShowDeleteConfirm(true)}
        onOpenScanModal={() => setShowScanModal(true)}
        onOpenStatusModal={openStatusModal}
        onOpenTerminateConfirm={() => setShowTerminateConfirm(true)}
        onPublishToDrive={handlePublishToDrive}
        publishing={publishing}
        onViewAgreement={agreementFile ? () => openFilePreview(agreementFile) : undefined}
      />

      <ContractDetailAlerts
        autoRenewWindow={autoRenewWindow}
        canWriteContracts={canWriteContracts}
        contract={c}
        daysToExp={daysToExp}
        daysToNoticeDate={daysToNoticeDate}
        dismissReminder={dismissReminder}
        isAlreadyExpired={isAlreadyExpired}
        isExpiringSoon={isExpiringSoon}
        markReviewed={handleMarkReviewed}
        noticeReminderDismissed={noticeReminderDismissed}
        noticeWindowClosed={noticeWindowClosed}
        noticeWindowOpen={noticeWindowOpen}
        openStatusModal={openStatusModal}
        renewalReminderDismissed={renewalReminderDismissed}
      />

      {/* ================= Fact strip ================= */}
      <ContractFactStrip
        canWriteContracts={canWriteContracts}
        contract={c}
        contractId={id}
        daysToExp={daysToExp}
        onOpenLawEditor={() => {
          setTab('clauses');
          const legalVenue = clauses.find((cl) => cl.label === 'Legal Venue');
          if (legalVenue) setOpenClause(legalVenue.num);
        }}
        termPct={termPct}
      />

      {/* ================= Tabs ================= */}
      <div className="segmented" style={{ marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.v} className={tab === t.v ? 'active' : ''} onClick={() => setTab(t.v)}>
            {t.l}
            {t.ct != null && t.ct > 0 && <span className="ct">{t.ct}</span>}
          </button>
        ))}
      </div>

      {/* ================= Detail grid ================= */}
      {/* The Structured Data tab is a verification workbench — it takes the
          full page width and drops the rail (doc-112); rail content stays one
          tab-click away. */}
      <div className={tab === 'data' ? 'detail-grid full' : 'detail-grid'}>
        <ContractDetailMainTabs
          canRunSchemas={can('run_schemas')}
          apiBase={apiBase}
          beginClauseEdit={beginClauseEdit}
          cancelClauseEdit={cancelClauseEdit}
          cancelFileEdit={cancelFileEdit}
          canWriteContracts={canWriteContracts}
          clauseDraft={clauseDraft}
          clauseError={clauseError}
          clauseSaving={clauseSaving}
          clauses={clauses}
          contract={c}
          contractId={id}
          deletingFileId={deletingFileId}
          editingClauseNum={editingClauseNum}
          editingFileId={editingFileId}
          fileDraft={fileDraft}
          fileTiles={fileTiles}
          handleMarkAsExecuted={handleMarkAsExecuted}
          markingFileId={markingFileId}
          onRegenerateKeyRisks={handleRegenerateKeyRisks}
          onClearKeyRisks={handleClearKeyRisks}
          onSaveKeyTerms={handleSaveKeyTerms}
          regeneratingKeyRisks={regeneratingKeyRisks}
          openClause={openClause}
          openFileDeleteConfirm={openFileDeleteConfirm}
          openFileEdit={openFileEdit}
          openFilePreview={openFilePreview}
          openUploadModal={openUploadModal}
          saveClauseEdit={saveClauseEdit}
          saveFileMeta={saveFileMeta}
          savingFileMeta={savingFileMeta}
          setClauseDraft={setClauseDraft}
          setDsDraft={setDsDraft}
          setFileDraft={setFileDraft}
          setOpenClause={setOpenClause}
          setShowDetailedSummaryModal={setShowDetailedSummaryModal}
          structuredData={structuredData}
          isAdmin={isAdmin}
          tab={tab}
          timeline={timeline}
        />

        {tab !== 'data' && (
          <ContractDetailRail
            additionalParties={additionalParties}
            canWriteContracts={canWriteContracts}
            contract={c}
            contractId={id}
            counterpartyContactEmail={counterpartyContactEmail}
            counterpartyContactName={counterpartyContactName}
            counterpartyContactPhone={counterpartyContactPhone}
            counterpartyContactRole={counterpartyContactRole}
            setTab={setTab}
            timeline={timeline}
          />
        )}
      </div>

      {preview?.contractId === id && (
        <ContractPreviewPanel
          contractId={id}
          files={filesForPreview}
          selectedKey={preview.key}
          onSelect={(key) => setPreview({ contractId: id, key })}
          onClose={() => setPreview(null)}
        />
      )}
      <ContractDetailModals
        canWriteContracts={canWriteContracts}
        closeFileDeleteConfirm={closeFileDeleteConfirm}
        contract={c}
        deleteContractDriveFolderWithLastFile={deleteContractDriveFolderWithLastFile}
        deleteContractWithLastFile={deleteContractWithLastFile}
        deleteDriveFile={deleteDriveFile}
        deleteDriveFolder={deleteDriveFolder}
        deleting={deleting}
        deletingFileId={deletingFileId}
        dsDraft={dsDraft}
        dsEditing={dsEditing}
        dsError={dsError}
        dsSaving={dsSaving}
        fileDeleteTarget={fileDeleteTarget}
        handleDelete={handleDelete}
        handleDeleteFile={handleDeleteFile}
        handleDetailedSummarySave={handleDetailedSummarySave}
        handleStatusChange={handleStatusChange}
        handleTerminate={handleTerminate}
        newExecutedDate={newExecutedDate}
        newStatus={newStatus}
        setDeleteContractDriveFolderWithLastFile={setDeleteContractDriveFolderWithLastFile}
        setDeleteContractWithLastFile={setDeleteContractWithLastFile}
        setDeleteDriveFile={setDeleteDriveFile}
        setDeleteDriveFolder={setDeleteDriveFolder}
        setDsDraft={setDsDraft}
        setDsEditing={setDsEditing}
        setNewExecutedDate={setNewExecutedDate}
        setNewStatus={setNewStatus}
        setShowDeleteConfirm={setShowDeleteConfirm}
        setShowDetailedSummaryModal={setShowDetailedSummaryModal}
        setShowStatusModal={setShowStatusModal}
        setShowTerminateConfirm={setShowTerminateConfirm}
        setTerminationReason={setTerminationReason}
        showDeleteConfirm={showDeleteConfirm}
        showDetailedSummaryModal={showDetailedSummaryModal}
        showStatusModal={showStatusModal}
        showTerminateConfirm={showTerminateConfirm}
        terminationReason={terminationReason}
      />
      <ContractUploadFileModal
        canWriteContracts={canWriteContracts}
        closeUploadModal={closeUploadModal}
        contractTitle={c.title}
        handleConfirmUpload={handleConfirmUpload}
        handleFilePicked={handleFilePicked}
        pendingUpload={pendingUpload}
        setPendingUpload={setPendingUpload}
        setUploadDragOver={setUploadDragOver}
        showUploadModal={showUploadModal}
        uploadAnalyzing={uploadAnalyzing}
        uploadBusy={uploadBusy}
        uploadDragOver={uploadDragOver}
      />
      {/* Scan */}
      <ScanDocumentModal
        isOpen={showScanModal}
        onClose={() => { setShowScanModal(false); setRescanInitialFile(null); }}
        contractId={id}
        apiBase={apiBase}
        initialFile={rescanInitialFile}
        hasStoredDocument={fileTiles.some((f) => f.fileId && f.fileKind !== 'reference' && f.tone !== 'folder')}
        onSuccess={(updated) => {
          setContract(updated);
          setRescanInitialFile(null);
          showToast('Contract updated from scan', 'success');
        }}
      />
    </div>
  );
}
